import React, { useEffect, useState } from "react";
import axios from "axios";
import { Alert, Col, Form, Input, Modal, Row } from "antd";
import { MailOutlined, PhoneOutlined, UserOutlined } from "@ant-design/icons";
import { createUser } from "../../../api/admin/userApi";
import { useNotifications } from "../../../context/NotificationContext";

/**
 * CU-86d44j2ac — creates a LOCAL customer through the generic user-creation capability with user_type =
 * customer; membership is decided server-side and no Shopify customer is created or synced.
 */
interface CreateCustomerModalProps {
	open: boolean;
	onClose: () => void;
	onCreated: () => void;
}

const CUSTOMER_USER_TYPE = "customer";

const CreateCustomerModal: React.FC<CreateCustomerModalProps> = ({ open, onClose, onCreated }) => {
	const [firstName, setFirstName] = useState("");
	const [middleName, setMiddleName] = useState("");
	const [lastName, setLastName] = useState("");
	const [username, setUsername] = useState("");
	const [email, setEmail] = useState("");
	const [phone, setPhone] = useState("");
	const [errors, setErrors] = useState<string[]>([]);
	const [saving, setSaving] = useState(false);
	const { showSuccess } = useNotifications();

	useEffect(() => {
		if (!open) return;

		setFirstName("");
		setMiddleName("");
		setLastName("");
		setUsername("");
		setEmail("");
		setPhone("");
		setErrors([]);
	}, [open]);

	const submit = async () => {
		setSaving(true);
		setErrors([]);

		try {
			await createUser({
				first_name: firstName.trim(),
				middle_name: middleName.trim() || undefined,
				last_name: lastName.trim(),
				username: username.trim(),
				email: email.trim(),
				phone: phone.trim(),
				user_type: CUSTOMER_USER_TYPE,
			} as never);

			showSuccess("Customer created successfully");
			onCreated();
			onClose();
		} catch (error) {
			if (axios.isAxiosError(error)) {
				const body = error.response?.data;
				const validationErrors = body?.errors ?? body;

				if (validationErrors && typeof validationErrors === "object") {
					const messages = Object.values(validationErrors)
						.flat()
						.filter((value): value is string => typeof value === "string");

					if (messages.length > 0) {
						setErrors(messages);
						return;
					}
				}
			}

			setErrors(["Failed to create the customer."]);
		} finally {
			setSaving(false);
		}
	};

	const canSubmit = Boolean(username.trim() && email.trim());

	return (
		<Modal
			open={open}
			title="Create Customer"
			okText="Create"
			onOk={submit}
			onCancel={onClose}
			confirmLoading={saving}
			okButtonProps={{ disabled: !canSubmit }}
			destroyOnClose
		>
			{errors.length > 0 && (
				<Alert
					type="error"
					style={{ marginBottom: 12 }}
					message="Errors"
					description={
						<ul style={{ margin: 0, paddingLeft: 20 }}>
							{errors.map((message, index) => (
								<li key={index}>{message}</li>
							))}
						</ul>
					}
					closable
					onClose={() => setErrors([])}
				/>
			)}

			<Form layout="vertical" size="small">
				<Row gutter={[8, 0]}>
					<Col xs={24} md={8}>
						<Form.Item label="Given Name">
							<Input
								aria-label="Given Name"
								value={firstName}
								onChange={(event) => setFirstName(event.target.value)}
							/>
						</Form.Item>
					</Col>
					<Col xs={24} md={8}>
						<Form.Item label="Middle Name">
							<Input
								aria-label="Middle Name"
								value={middleName}
								onChange={(event) => setMiddleName(event.target.value)}
							/>
						</Form.Item>
					</Col>
					<Col xs={24} md={8}>
						<Form.Item label="Family Name">
							<Input
								aria-label="Family Name"
								value={lastName}
								onChange={(event) => setLastName(event.target.value)}
							/>
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item label="Username" required>
							<Input
								aria-label="Username"
								prefix={<UserOutlined />}
								value={username}
								onChange={(event) => setUsername(event.target.value)}
							/>
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item label="Email" required>
							<Input
								aria-label="Email"
								type="email"
								prefix={<MailOutlined />}
								value={email}
								onChange={(event) => setEmail(event.target.value)}
							/>
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item label="Phone">
							<Input
								aria-label="Phone"
								prefix={<PhoneOutlined />}
								value={phone}
								onChange={(event) => setPhone(event.target.value)}
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>
		</Modal>
	);
};

export default CreateCustomerModal;
